import { useEffect, useRef } from "react";
import {
  acquireTexture as acquireTextureFromCache,
  release as releaseAsset,
} from "../../runtime/assetCache";
import {
  applyMaterialTransparencySettings,
  buildMaterial,
  applyColorMapToTexture,
  applyTextureRoleSettings,
} from "../materialFactory";
import { getSceneState } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";

const MATERIAL_TEXTURE_FIELDS = [
  ["colorMap", "map", "color"],
  ["alphaMap", "alphaMap", "alpha"],
  ["normalMap", "normalMap", "normal"],
  ["roughnessMap", "roughnessMap", "roughness"],
  ["metalnessMap", "metalnessMap", "metalness"],
  ["aoMap", "aoMap", "ao"],
  ["emissiveMap", "emissiveMap", "emissive"],
  ["bumpMap", "bumpMap", "bump"],
  ["displacementMap", "displacementMap", "displacement"],
  ["clearcoatMap", "clearcoatMap", "data"],
  ["clearcoatNormalMap", "clearcoatNormalMap", "normal"],
  ["clearcoatRoughnessMap", "clearcoatRoughnessMap", "roughness"],
  ["transmissionMap", "transmissionMap", "data"],
  ["thicknessMap", "thicknessMap", "data"],
];

const releaseMaterialTextures = (material) => {
  const textureIds = new Set();
  if (material?.userData?.assetTextureId) textureIds.add(material.userData.assetTextureId);
  Object.values(material?.userData?.assetTextureIds || {}).forEach((id) => {
    if (id) textureIds.add(id);
  });
  textureIds.forEach((id) => {
    try {
      releaseAsset(id);
    } catch (error) {
      logger.warn("[useMaterialSync] Failed to release previous material texture", error);
    }
  });
};

const applyMaterialTextureSlots = (material, materialDef, texturesState) => {
  if (!material || !materialDef || !texturesState) return;
  MATERIAL_TEXTURE_FIELDS.forEach(([storeField, threeField, role]) => {
    const textureUuid = materialDef[storeField];
    const fingerprint = textureUuid ? texturesState[textureUuid]?.fingerprint || null : null;
    if (!fingerprint) return;
    acquireTextureFromCache(fingerprint)
      .then((tex) => {
        const clone = tex.clone();
        applyTextureRoleSettings(clone, role);
        if (storeField === "colorMap") {
          applyColorMapToTexture(clone, materialDef, texturesState[textureUuid] || null);
        }
        material[threeField] = clone;
        material.userData = material.userData || {};
        material.userData.assetTextureIds = {
          ...(material.userData.assetTextureIds || {}),
          [threeField]: fingerprint,
        };
        if (threeField === "map") {
          material.userData.assetTextureId = fingerprint;
        }
        applyMaterialTransparencySettings(material, materialDef);
        material.needsUpdate = true;
      })
      .catch((error) => {
        logger.warn("[useMaterialSync] Failed to acquire material texture", {
          material: materialDef.name,
          field: storeField,
          error,
        });
      });
  });
};

const collectChangedMaterialIds = (prev = {}, next = {}) => {
  const changed = new Set();

  Object.keys(next).forEach((materialId) => {
    if (prev[materialId] !== next[materialId]) {
      changed.add(materialId);
    }
  });

  Object.keys(prev).forEach((materialId) => {
    if (!(materialId in next)) {
      const prevMap = prev[materialId]?.colorMap ?? null;
      if (prevMap) changed.add(materialId);
    }
  });

  return changed;
};

export function useMaterialSync({ materials, sceneObjectsRef, sceneObjects }) {
  const prevMaterialsRef = useRef(null);
  const materialMatchesTarget = (threeMaterial, child, materialId, materialName) => {
    if (!threeMaterial) return false;
    // If we have a concrete material UUID, match strictly on UUID to avoid
    // accidentally updating a different material that shares a display name.
    if (materialId) return child?.userData?.materialUuid === materialId;
    if (materialName && threeMaterial.name === materialName) return true;
    return false;
  };

  // Detect colorMap changes on materials and update scene materials in-place
  useEffect(() => {
    try {
      const prev = prevMaterialsRef.current || {};
      const next = materials;

      const changed = collectChangedMaterialIds(prev, next);

      if (changed.size > 0) {
        const objectsMap = sceneObjectsRef.current || {};
        const texturesState = getSceneState().textures;
        const updateForMaterial = (materialId) => {
          const matDef = next[materialId] || prev[materialId];
          const materialName = matDef?.name || materialId;
          Object.values(objectsMap).forEach((obj) => {
            if (!obj || typeof obj.traverse !== "function") return;
            obj.traverse((child) => {
              if (!child || !child.isMesh || !child.material) return;
              const isArrayMaterial = Array.isArray(child.material);
              const applyTo = isArrayMaterial ? child.material : [child.material];
              applyTo.forEach((m, index) => {
                if (!m) return;
                if (!materialMatchesTarget(m, child, materialId, materialName)) return;
                releaseMaterialTextures(m);
                const rebuilt = buildMaterial(materialName, matDef, () => null);
                rebuilt.userData = {
                  ...(rebuilt.userData || {}),
                  materialUuid: materialId,
                };
                applyMaterialTextureSlots(rebuilt, matDef, texturesState);
                if (isArrayMaterial) {
                  child.material[index] = rebuilt;
                } else {
                  child.material = rebuilt;
                }
                child.userData = { ...(child.userData || {}), materialUuid: materialId };
              });
            });
          });
        };

        changed.forEach((materialId) => {
          updateForMaterial(materialId);
        });
      }

      prevMaterialsRef.current = materials;
    } catch (e) {
      logger.warn("[Scene] Texture swap handling failed", e);
    }
  }, [materials, sceneObjectsRef]);

  // Material property updates event
  useEffect(() => {
    const handleMaterialUpdate = (event) => {
      const { materialId, materialName: eventMaterialName, updates } = event.detail || {};
      if (!updates) return;
      // Always get the latest materials directly from the store
      const latestMaterials = getSceneState().materials;
      const matDef =
        (materialId && latestMaterials[materialId]) ||
        (eventMaterialName
          ? Object.values(latestMaterials).find((mat) => mat?.name === eventMaterialName)
          : null);
      const targetName = matDef?.name || eventMaterialName;
      if (!targetName && !materialId) return;

      Object.values(sceneObjects || {}).forEach((obj) => {
        if (!obj || typeof obj.traverse !== "function") return;
        obj.traverse((child) => {
          if (!child.isMesh || !child.material) return;
          const applyTo = Array.isArray(child.material) ? child.material : [child.material];
          const matched = applyTo.filter((m) =>
            materialMatchesTarget(m, child, materialId, targetName)
          );
          if (matched.length === 0) return;

          const mat = matched[0];
          const texturesState = getSceneState().textures;
          const mapName = updates.colorMap ?? matDef?.colorMap ?? null;
          const fingerprint = mapName ? texturesState[mapName]?.fingerprint || null : null;

          if (updates.kind && matDef) {
            const existingMap = mat.map || null;
            const next = buildMaterial(targetName, matDef, () => existingMap);
            if (existingMap) next.map = existingMap;
            if (mat.userData?.assetTextureId) {
              next.userData = { ...next.userData, assetTextureId: mat.userData.assetTextureId };
            }
            child.material = next;
            child.material.needsUpdate = true;
            return;
          }

          // Check if we need to rebuild due to material type mismatch
          const shouldBePhysical =
            matDef &&
            (matDef.kind === "physical" ||
              (matDef.clearcoat && matDef.clearcoat > 0) ||
              (matDef.transmission && matDef.transmission > 0) ||
              (matDef.thickness && matDef.thickness > 0) ||
              (matDef.ior && matDef.ior !== 1.45));

          if (shouldBePhysical && matDef && !mat.isMeshPhysicalMaterial) {
            const existingMap = mat.map || null;
            const next = buildMaterial(targetName, matDef, () => existingMap);
            if (existingMap) next.map = existingMap;
            if (mat.userData?.assetTextureId) {
              next.userData = { ...next.userData, assetTextureId: mat.userData.assetTextureId };
            }
            child.material = next;
            child.material.needsUpdate = true;
            return;
          }

          if (updates.colorMap !== undefined) {
            try {
              const oldId = mat.userData && mat.userData.assetTextureId;
              if (oldId && oldId !== fingerprint) {
                releaseAsset(oldId);
                if (mat.map) {
                  mat.map = null;
                }
                if (mat.userData) delete mat.userData.assetTextureId;
              }
            } catch (error) {
              logger.warn(
                "[useMaterialSync] Failed to release old texture during colorMap update",
                error
              );
            }

            if (fingerprint) {
              acquireTextureFromCache(fingerprint)
                .then((tex) => {
                  mat.map = tex.clone();
                  const texMeta = mapName ? texturesState[mapName] || null : null;
                  applyColorMapToTexture(mat.map, matDef, texMeta);
                  applyMaterialTransparencySettings(mat, matDef);
                  mat.userData = mat.userData || {};
                  mat.userData.assetTextureId = fingerprint;
                  mat.needsUpdate = true;
                })
                .catch((error) => {
                  logger.warn(
                    "[useMaterialSync] Failed to acquire updated colorMap texture",
                    error
                  );
                });
            } else if (mat.map) {
              mat.map = null;
              mat.needsUpdate = true;
            }
          }

          if (
            (updates.colorMapWrap !== undefined || updates.colorMapRepeat !== undefined) &&
            mat.map
          ) {
            // mat.map is already a clone owned by this material, so mutating
            // repeat/wrap only affects this material.
            const texMeta = mapName ? texturesState[mapName] || null : null;
            applyColorMapToTexture(mat.map, matDef, texMeta);
          }

          if (updates.color) {
            const [r, g, b, a] = updates.color;
            if (mat.color && typeof mat.color.setRGB === "function") {
              mat.color.setRGB(r ?? mat.color.r, g ?? mat.color.g, b ?? mat.color.b);
            }
            if (a != null) {
              mat.opacity = a;
              mat.transparent = updates.transparent != null ? updates.transparent : a < 1;
            }
          }

          if (updates.opacity != null) {
            mat.opacity = updates.opacity;
            mat.transparent =
              updates.transparent != null ? updates.transparent : updates.opacity < 1;
          }

          if (updates.transparent != null) {
            mat.transparent = updates.transparent;
          }

          if (
            updates.transparent !== undefined ||
            updates.opacity !== undefined ||
            updates.color !== undefined ||
            updates.colorMap !== undefined
          ) {
            applyMaterialTransparencySettings(mat, matDef);
          }

          const metalnessUpdate =
            updates.metalness !== undefined
              ? Number(updates.metalness)
              : updates.metallic !== undefined
                ? Number(updates.metallic)
                : undefined;
          if (metalnessUpdate !== undefined && mat.metalness !== undefined) {
            mat.metalness = metalnessUpdate;
          }

          const roughnessUpdate =
            updates.roughness !== undefined ? Number(updates.roughness) : undefined;
          if (roughnessUpdate !== undefined && mat.roughness !== undefined) {
            mat.roughness = roughnessUpdate;
          }

          // Handle physics properties for MeshPhysicalMaterial
          if (updates.clearcoat !== undefined && mat.clearcoat !== undefined) {
            mat.clearcoat = Number(updates.clearcoat);
          }
          if (updates.clearcoatRoughness !== undefined && mat.clearcoatRoughness !== undefined) {
            mat.clearcoatRoughness = Number(updates.clearcoatRoughness);
          }
          if (updates.transmission !== undefined && mat.transmission !== undefined) {
            mat.transmission = Number(updates.transmission);
          }
          if (updates.thickness !== undefined && mat.thickness !== undefined) {
            mat.thickness = Number(updates.thickness);
          }
          if (updates.ior !== undefined && mat.ior !== undefined) {
            mat.ior = Number(updates.ior);
          }
          if (updates.shininess !== undefined && mat.isMeshPhongMaterial) {
            mat.shininess = updates.shininess;
          }
          if (updates.specular && mat.isMeshPhongMaterial && mat.specular) {
            mat.specular.setRGB(
              updates.specular[0] ?? mat.specular.r,
              updates.specular[1] ?? mat.specular.g,
              updates.specular[2] ?? mat.specular.b
            );
          }

          mat.needsUpdate = true;
        });
      });
    };
    window.addEventListener("material-updated", handleMaterialUpdate);
    return () => window.removeEventListener("material-updated", handleMaterialUpdate);
  }, [sceneObjects, materials]);

  // Material assignment changes (mesh -> material rebind)
  useEffect(() => {
    const handleMaterialAssignmentChange = (event) => {
      const detail = event?.detail || {};
      const meshId = detail.meshName;
      const materialKey = detail.material;
      if (!meshId || !materialKey) return;

      const mats = getSceneState().materials;
      const mat =
        (materialKey && mats[materialKey]) ||
        Object.values(mats).find((entry) => entry?.name === materialKey);
      if (!mat) return;
      const materialName = mat.name || materialKey;

      const definition = { ...mat, name: materialName };
      const next = buildMaterial(materialName, definition, () => null);
      const texturesState = getSceneState().textures;
      const mapName = definition.colorMap ?? null;
      const fingerprint = mapName ? texturesState[mapName]?.fingerprint || null : null;

      if (fingerprint) {
        acquireTextureFromCache(fingerprint)
          .then((tex) => {
            next.map = tex.clone();
            const texMeta = mapName ? texturesState[mapName] || null : null;
            applyColorMapToTexture(next.map, definition, texMeta);
            applyMaterialTransparencySettings(next, definition);
            next.userData = { ...next.userData, assetTextureId: fingerprint };
            next.needsUpdate = true;
          })
          .catch(() => {});
      }

      Object.values(sceneObjects || {}).forEach((obj) => {
        if (!obj || typeof obj.traverse !== "function") return;
        obj.traverse((child) => {
          if (child && child.isMesh && child.userData?.meshName === meshId) {
            try {
              const prevMat = child.material;
              const prevList = Array.isArray(prevMat) ? prevMat : [prevMat];
              prevList.forEach((m) => {
                const texId = m?.userData?.assetTextureId;
                if (texId) {
                  releaseAsset(texId);
                  if (m.map) m.map = null;
                  if (m.userData) delete m.userData.assetTextureId;
                }
              });
            } catch (error) {
              logger.warn(
                "[useMaterialSync] Failed to release textures during material reassignment",
                error
              );
            }
            child.material = next;
            child.userData = {
              ...(child.userData || {}),
              materialUuid: mat.uuid || materialKey,
            };
            child.material.needsUpdate = true;
          }
        });
      });
    };
    window.addEventListener("material-assignment-changed", handleMaterialAssignmentChange);
    return () =>
      window.removeEventListener("material-assignment-changed", handleMaterialAssignmentChange);
  }, [sceneObjects]);
}
